// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentStatus.test.tsx
//
// The open comment's Status (Figma November 2026, frame 0c "Comment drawer ·
// Not relevant + reason"): Relevant | Not relevant as one segmented control.
// Picking Not relevant opens where the memory applies and why, and Save sends it.
import { Button, Select, Text, TextField } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type RejectionChoice,
  REJECTION_REASON_MAX_CHARS,
} from "./AskV2ReviewRejectScopePrompt";

/** Where a Not relevant memory applies, as the drawer offers it. */
export type RejectionScope = "enterprise" | "review";

// why: christo knows two reaches. "rule" is the enterprise rule she confirms in
// the chat; "once" clears the comment for this review and teaches nothing,
// until a project-scoped memory exists on the backend.
const REACH_OF: Record<RejectionScope, RejectionChoice["reach"]> = {
  enterprise: "rule",
  review: "once",
};

const SEGMENT_CLASS =
  "flex h-full items-center rounded-lg border px-3 text-[12px] leading-4 text-puntt-neutral-gray-12";
const ON_CLASS = "border-puntt-neutral-gray-7 bg-base-white font-medium";
const OFF_CLASS = "border-transparent bg-transparent";

export type AskV2ReviewCommentStatusProps = {
  commentId: string;
  liked?: boolean | null;
  /** Rejected here or in a past review; Not relevant then restores it. */
  rejected: boolean;
  disabled?: boolean;
  onJudge: (liked: boolean | null, rejection?: RejectionChoice) => void;
};

export function AskV2ReviewCommentStatus({
  commentId,
  liked = null,
  rejected,
  disabled = false,
  onJudge,
}: AskV2ReviewCommentStatusProps) {
  const { t } = useTranslation("ui");
  const [asking, setAsking] = useState(false);
  const [scope, setScope] = useState<RejectionScope>("enterprise");
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  // why: while she says why it isn't relevant, only Not relevant reads as picked.
  const relevant = liked === true && !asking;
  const notRelevant = rejected || asking;
  const headingId = `ask-v2-review-detail-status-${commentId}`;
  const reasonId = `ask-v2-review-status-reason-${commentId}`;
  // why: an enterprise rule is read from her reason, so christo refuses one without it.
  const canSave = scope === "review" || trimmed !== "";
  // why: a this-review call teaches nothing, so no refresh reads its reason.
  const reasonLabel = t(
    scope === "review"
      ? "askV2.review.drawer.reasonLabelOnce"
      : "askV2.review.drawer.reasonLabel",
  );

  const save = () => {
    if (!canSave) return;
    setAsking(false);
    setReason("");
    onJudge(false, { reach: REACH_OF[scope], reason: trimmed });
  };

  return (
    <div
      className="flex flex-col gap-2"
      role="group"
      aria-labelledby={headingId}
      data-testid={`ask-v2-review-status-${commentId}`}
    >
      <Text
        as="p"
        size="1"
        className="text-puntt-neutral-gray-11"
        id={headingId}
      >
        {t("askV2.review.drawer.status")}
      </Text>
      <div className="inline-flex h-6 items-stretch self-start rounded-lg border border-puntt-neutral-gray-7 bg-puntt-neutral-gray-3">
        <button
          type="button"
          className={cx(SEGMENT_CLASS, relevant ? ON_CLASS : OFF_CLASS)}
          aria-pressed={relevant}
          disabled={disabled}
          data-testid={`ask-v2-review-status-relevant-${commentId}`}
          onClick={() => {
            setAsking(false);
            // why: on an Approved comment this only closes the form; it is already relevant.
            if (asking && liked === true) return;
            onJudge(relevant ? null : true);
          }}
        >
          {t("askV2.review.rowRelevant")}
        </button>
        <div
          className={cx(
            "flex items-center rounded-lg border",
            notRelevant ? ON_CLASS : OFF_CLASS,
          )}
        >
          <button
            type="button"
            // why: carrie has no preflight, so a bare button keeps the browser's gray fill.
            className={cx(SEGMENT_CLASS, "border-transparent bg-transparent")}
            aria-pressed={notRelevant}
            disabled={disabled}
            data-testid={`ask-v2-review-status-not-relevant-${commentId}`}
            onClick={() => {
              // why: on a rejected comment this is the way back, so it restores
              // at once instead of asking why a second time.
              if (rejected) onJudge(null);
              else setAsking((open) => !open);
            }}
          >
            {t("askV2.review.rowNotRelevant")}
          </button>
          {asking ? (
            <Select.Root
              size="1"
              value={scope}
              onValueChange={(value) => setScope(value as RejectionScope)}
            >
              <Select.Trigger
                variant="ghost"
                color="gray"
                className="mr-1"
                aria-label={t("askV2.review.drawer.scopeLabel")}
                data-testid={`ask-v2-review-status-scope-${commentId}`}
              />
              <Select.Content position="popper">
                <Select.Item
                  value="enterprise"
                  data-testid={`ask-v2-review-status-scope-enterprise-${commentId}`}
                >
                  {t("askV2.review.drawer.scope.enterprise")}
                </Select.Item>
                <Select.Item
                  value="review"
                  data-testid={`ask-v2-review-status-scope-review-${commentId}`}
                >
                  {t("askV2.review.drawer.scope.review")}
                </Select.Item>
              </Select.Content>
            </Select.Root>
          ) : null}
        </div>
      </div>
      {asking ? (
        <form
          className="flex flex-col gap-2"
          aria-label={reasonLabel}
          data-testid={`ask-v2-review-status-reason-form-${commentId}`}
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <label
            htmlFor={reasonId}
            className="text-[14px] font-medium leading-5 text-puntt-neutral-gray-12"
          >
            {reasonLabel}
          </label>
          <TextField.Root
            id={reasonId}
            data-testid={reasonId}
            size="2"
            className="ph-no-capture"
            placeholder={t("askV2.review.rowReasonPlaceholder")}
            maxLength={REJECTION_REASON_MAX_CHARS}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button
            type="submit"
            size="1"
            className="self-start"
            disabled={disabled || !canSave}
            data-testid={`ask-v2-review-status-save-${commentId}`}
          >
            {t("askV2.review.rowReasonSave")}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
